import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getActive,
  getDropshipDetail,
  refreshTracking,
  manualTracking,
  markManual,
  cancelDropship,
  completeDropship,
  repostTracking,
  updateForm5106,
  type ManualTrackingInput,
  type Form5106Action,
} from '@/lib/api/active'

/** Active Dropships data. Server pre-sorts newest first; we render in that order.
 * The flag is part of the key so each view caches separately; the mutations all
 * invalidate the ['active'] prefix, which covers both. */
export function useActive(includeRetired = false) {
  return useQuery({
    queryKey: ['active', includeRetired],
    queryFn: () => getActive(includeRetired),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })
}

/** Persisted submission detail for one dropship order (read-only). */
export function useDropshipDetail(id: number) {
  return useQuery({
    queryKey: ['dropship-detail', id],
    queryFn: () => getDropshipDetail(id),
  })
}

/** Trigger an immediate KH poll — global (no id) or for a single order. */
export function useRefreshTracking() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id?: number) => refreshTracking(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['active'] })
    },
  })
}

export function useManualTracking() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: ManualTrackingInput }) =>
      manualTracking(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['active'] })
    },
  })
}

export function useMarkManual() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, kh_reference }: { id: number; kh_reference: string }) =>
      markManual(id, kh_reference),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['active'] })
    },
  })
}

/** Cancel returns the order to the queue, so invalidate both caches. */
export function useCancelDropship() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => cancelDropship(id, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['active'] })
      void queryClient.invalidateQueries({ queryKey: ['queue'] })
    },
  })
}

/** Complete removes the order from the Active board; refresh both caches. */
export function useCompleteDropship() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => completeDropship(id),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: ['active'] })
      void queryClient.invalidateQueries({ queryKey: ['dropship-detail', id] })
    },
  })
}

export function useRepostTracking() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, shopify_order_id }: { id: number; shopify_order_id: string }) =>
      repostTracking(id, shopify_order_id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['active'] })
    },
  })
}

export function useUpdateForm5106() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, action }: { id: number; action: Form5106Action }) =>
      updateForm5106(id, action),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['active'] })
    },
  })
}
